/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    PerformanceEvents as Events,
    PerformanceEvent as Event,
} from "@azure/msal-browser";

/**
 * Temporary solution to accommodate both 1p and 3p telemetry functionality.
 */

export const PerformanceEvents = {
    ...Events,
    PairwiseBrokerApplicationInitializeBrokering:
        "pairwiseBrokerApplicationInitializeBrokering",
    BrokerClientApplicationHandleBrokerHandshake:
        "brokerClientApplicationHandleBrokerHandshake",
    BrokerClientApplicationBrokeredSSOSilentRequest:
        "brokerClientApplicationBrokeredSSOSilentRequest",
    BrokerClientApplicationBrokeredSilentRequest:
        "brokerClientApplicationBrokeredSilentRequest",
    EmbeddedClientApplicationSendSSOSilentRequest: "embeddedSSOSilent",
    EmbeddedClientApplicationSendPopupRequest: "embeddedPopup",
    EmbeddedClientApplicationSendRedirectRequest: "embeddedRedirect",
    EmbeddedClientApplicationHandleRedirectRequest: "embeddedHandleRedirect",
    EmbeddedClientApplicationSendSilentRefreshRequest: "embeddedSilentRefresh",
    EmbeddedSendSilentRefreshRequestInternal: "embeddedSilentRefreshInternal",
    SendHandshakeRequest: "sendHandshakeRequest",
    SendMessageToBroker: "sendMessageToBroker",
    SendRequest: "sendRequest",
    HandleResponse: "handleBrokerResponse",
    GetFrameDepth: "getFrameDepth",
    OnHandshakeResponse: "onHandshakeResponse",
    HandleBrokerMessage: "handleBrokerMessage",
    PostMessageToAllDescendantFrames: "postMessageToAllDescendantFrames",
    HandleBrokerAuthRequest: "handleBrokerAuthRequest",
    BrokeredSilentRequest: "brokeredSilentRequest",
    BrokeredSsoSilentRequest: "brokeredSsoSilentRequest",
    BrokeredRedirectRequest: "brokeredRedirectRequest",
    BrokeredPopupRequest: "brokeredPopupRequest",
    InteractiveBrokerRequest: "interactiveBrokerRequest",
} as const;
export type PerformanceEvents =
    (typeof PerformanceEvents)[keyof typeof PerformanceEvents];

export type PerformanceCallbackFunction = (events: PerformanceEvent[]) => void;

export const PerformanceEventAbbreviations: ReadonlyMap<string, string> =
    new Map([
        [
            PerformanceEvents.PairwiseBrokerApplicationInitializeBrokering,
            "PWBInitBrk",
        ],
        [
            PerformanceEvents.BrokerClientApplicationHandleBrokerHandshake,
            "BrkClientHandeBrkHandshake",
        ],
        [
            PerformanceEvents.BrokerClientApplicationBrokeredSSOSilentRequest,
            "BrkClientBrkSSOReq",
        ],
        [
            PerformanceEvents.BrokerClientApplicationBrokeredSilentRequest,
            "BrkClientBrkSltReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
            "EmbClientSendSSOReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
            "EmbClientSendPopupReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
            "EmbClientSendRedirectReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
            "EmbClientHandleRedirectReq",
        ],
        [
            PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
            "EmbClientSendSltRefreshReq",
        ],
        [PerformanceEvents.SendHandshakeRequest, "sendHandshakeReq"],
        [PerformanceEvents.SendMessageToBroker, "sendMsgToBrk"],
        [PerformanceEvents.SendRequest, "sendReq"],
        [PerformanceEvents.HandleResponse, "handleBrkRes"],
        [PerformanceEvents.GetFrameDepth, "getFrameDepth"],
        [PerformanceEvents.OnHandshakeResponse, "onHandshakeRes"],
        [PerformanceEvents.HandleBrokerMessage, "handleBrkMsg"],
        [
            PerformanceEvents.PostMessageToAllDescendantFrames,
            "postMsgToDescFrames",
        ],
        [PerformanceEvents.HandleBrokerAuthRequest, "handleBrkAuthReq"],
        [PerformanceEvents.BrokeredSilentRequest, "brkSltReq"],
        [PerformanceEvents.BrokeredSsoSilentRequest, "brkSsoSltReq"],
        [PerformanceEvents.BrokeredRedirectRequest, "brkRedirectReq"],
        [PerformanceEvents.BrokeredPopupRequest, "brkPopupSltReq"],
        [PerformanceEvents.InteractiveBrokerRequest, "intBrkReq"],
    ]);

export type PerformanceEvent = Event & {
    brokerInteractionTimeoutMs?: number;
    brokerMessageTimeoutMs?: number;
    brokerHandshakeTimeoutMs?: number;
    brokerIframeTimeoutMs?: number;
    brokerIframeTimedOut?: boolean;
    synthetic?: boolean;
    // Profile telemetry identifier for MATS
    profileTelemetryId?: string;
    // Session identifier for MATS
    sessionId?: string;
    // Broker app indicator
    isBroker?: boolean;
    // For internal use to route broker events (ats, sso, popup, redirect) properly
    brokerEventName?: PerformanceEvents;
    // Embedded app id
    embeddedClientId?: string;
    // Embedded redirect uri
    embeddedRedirectUri?: string;
    // Whether this event was executed in the background
    isBackground?: boolean;
    // The maximum iframe depth notified about broker availability
    maxFrameDepthReached?: number;
    // The maximum iframe depth configured for broker availability events
    maxFrameDepthConfigured?: number;
    // The number of frames notified about broker availability
    framesNotified?: number;
    // Number of handshake responses ignored due to nonce mismatch
    handshakeIgnoredCount?: number;
    // Broker frame depth
    brokerFrameDepth?: number;
    // Broker reconnected during request
    brokerConnReset?: boolean;
    // Number of broker instances in parent frames
    parentBrokerCount?: number;
    // Session ids
    embeddedSessionId?: string;
    brokerSessionId?: string;
    messageId?: string;
    resolverKeyMissingCount?: number;
    resolverKeyNotExistCount?: number;
};

export const intFields: ReadonlySet<string> = new Set([
    "brokerInteractionTimeoutMs",
    "brokerMessageTimeoutMs",
    "brokerHandshakeTimeoutMs",
    "brokerIframeTimeoutMs",
    "maxFrameDepthReached",
    "maxFrameDepthConfigured",
    "framesNotified",
    "handshakeIgnoredCount",
    "brokerFrameDepth",
    "resolverKeyMissingCount",
    "resolverKeyNotExistCount",
]);
